import { Link } from "react-router-dom";
import { type FieldNote, islandLabel } from "@/lib/fieldNotes";

interface FieldNoteCardProps {
  note: FieldNote;
  priority?: boolean;
  /**
   * If provided, the card links to the article with ?tag=... so the
   * essay page can offer a "Back to {tag}" return path. Keeps the
   * archive's filtered context intact across navigation.
   */
  returnTag?: string;
}

/**
 * Editorial card used across the homepage gateway, archive grid
 * and "Continue Wandering" related list. Cinematic museum rhythm:
 * generous whitespace, restrained typography, slow hover.
 */
const FieldNoteCard = ({ note, priority, returnTag }: FieldNoteCardProps) => {
  const href = returnTag
    ? `/field-notes/${note.slug}?tag=${encodeURIComponent(returnTag)}`
    : `/field-notes/${note.slug}`;
  return (
    <Link
      to={href}
      className="group block"
      aria-label={`Read field note: ${note.title}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-sm mb-7 md:mb-8 bg-[hsl(150_35%_10%)]">
        <img
          src={note.heroImage}
          alt={note.title}
          loading={priority ? "eager" : "lazy"}
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-[1200ms] ease-out"
        />
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-t from-[hsl(150_35%_6%)]/95 via-[hsl(150_35%_6%)]/55 to-transparent"
        />
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
          <p className="font-sans text-[10px] uppercase tracking-[0.32em] text-[hsl(38_75%_70%)] mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
            {islandLabel(note.island)}
          </p>
          <p className="font-serif italic text-[hsl(40_33%_97%)] text-[13px] md:text-[14px] leading-snug drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]">
            {note.dek}
          </p>
        </div>
      </div>
      <h3 className="font-serif text-[1.35rem] md:text-2xl text-foreground leading-[1.25] mb-3 md:mb-4 group-hover:text-accent transition-colors duration-500">
        {note.title}
      </h3>
      <p className="font-sans text-[13.5px] md:text-[14px] text-foreground/65 leading-[1.7] line-clamp-3">
        {note.excerpt}
      </p>
      <span className="inline-block mt-5 md:mt-6 font-sans text-[10px] tracking-[0.32em] uppercase text-accent group-hover:underline underline-offset-4">
        Read the field note →
      </span>
    </Link>
  );
};

export default FieldNoteCard;
